import type { PopupAd } from "@/types";
import { popupPageLabel } from "@/config/popupPages";
import { popupAdStatus, POPUP_TONE_CLASS, shortDate } from "@/pages/admin/popup-ads/utils";

interface PopupAdCardProps {
  ad: PopupAd;
  onEdit: (ad: PopupAd) => void;
  onDelete: (ad: PopupAd) => void;
  onToggle: (ad: PopupAd) => void;
}

/** One popup ad in the admin list: media thumb, targeting and quick actions. */
export default function PopupAdCard({ ad, onEdit, onDelete, onToggle }: PopupAdCardProps) {
  const status = popupAdStatus(ad);

  return (
    <article className="rounded-lg border border-background-200 bg-background-50 p-4">
      <div className="flex gap-4">
        <div className="relative h-20 w-28 shrink-0 overflow-hidden rounded-md bg-foreground-950">
          {ad.mediaType === "video" ? (
            <div className="flex h-full w-full items-center justify-center text-background-50">
              <i className="ri-play-circle-line text-3xl"></i>
            </div>
          ) : ad.mediaUrl ? (
            <img src={ad.mediaUrl} alt={ad.title} className="h-full w-full object-cover object-top" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-background-50">
              <i className="ri-image-line text-2xl"></i>
            </div>
          )}
          <span className="absolute bottom-1 left-1 rounded bg-foreground-950/70 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-background-50">
            {ad.mediaType}
          </span>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="truncate font-semibold text-foreground-950">{ad.title}</h3>
            <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold ${POPUP_TONE_CLASS[status.tone]}`}>
              {status.label}
            </span>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {ad.targetPages.map((page) => (
              <span key={page} className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-semibold text-secondary-900">
                {popupPageLabel(page)}
              </span>
            ))}
          </div>

          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-foreground-500 sm:grid-cols-4">
            <span>
              <i className="ri-repeat-line mr-1"></i>
              {ad.frequencyCap <= 0 ? "Unlimited" : `${ad.frequencyCap}x`}
            </span>
            <span>
              <i className="ri-timer-line mr-1"></i>
              {ad.delaySeconds}s delay
            </span>
            <span>
              <i className="ri-eye-line mr-1"></i>
              {ad.impressions.toLocaleString()} views
            </span>
            <span>
              <i className="ri-sort-desc mr-1"></i>
              Priority {ad.priority}
            </span>
          </div>

          {(ad.startAt || ad.endAt) && (
            <p className="mt-1.5 text-xs text-foreground-500">
              <i className="ri-calendar-line mr-1"></i>
              {ad.startAt ? shortDate(ad.startAt) : "now"} → {ad.endAt ? shortDate(ad.endAt) : "no end date"}
            </p>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-background-100 pt-3">
        <button
          type="button"
          onClick={() => onToggle(ad)}
          className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-xs font-semibold text-foreground-700 hover:border-primary-400 whitespace-nowrap cursor-pointer"
        >
          <i className={ad.isActive ? "ri-pause-line" : "ri-play-line"}></i>
          {ad.isActive ? "Disable" : "Enable"}
        </button>
        <button
          type="button"
          onClick={() => onEdit(ad)}
          className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-xs font-semibold text-foreground-700 hover:border-primary-400 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-pencil-line"></i> Edit
        </button>
        <button
          type="button"
          onClick={() => onDelete(ad)}
          className="ml-auto inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold text-secondary-700 hover:bg-secondary-50 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-delete-bin-line"></i> Remove
        </button>
      </div>
    </article>
  );
}